import { Children, forwardRef, useId, useMemo } from 'react';
import { ChakraStylesConfig, CreatableSelect, Props as SelectProps, PropsValue, Select } from 'chakra-react-select';
import { SizeProp } from 'chakra-react-select/dist/types/types';
import VirtualList, { ItemInfo } from 'react-tiny-virtual-list';

import { Box } from '@chakra-ui/react';

import { useI18n } from '~/hooks/use-i18n';
import { SelectOption } from '~/types/select-option';

import { countOptionElements, findSelectedOptionIndex } from './select-input-options';

export type SelectInputProps = {
  name: string;
  options: SelectOption[];
  defaultValue?: PropsValue<SelectOption>;
  placeholder?: string;
  selectProps?: SelectProps;
  isVirtual?: boolean;
  controlId?: string;
  isCreatable?: boolean;
  openMenuOnFocus?: boolean;
  isRequired?: boolean;
  controlBorderColor?: string;
};

export const SelectInput = forwardRef<any, SelectInputProps>(
  (
    {
      name,
      options,
      defaultValue,
      placeholder,
      selectProps,
      controlId,
      isVirtual = true,
      isCreatable = false,
      openMenuOnFocus = true,
      isRequired = false,
      controlBorderColor,
    },
    ref
  ) => {
    const inputId = useId();
    const InputComponent = useMemo(() => (isCreatable ? CreatableSelect : Select), [isCreatable]);
    return (
      <InputComponent
        ref={ref}
        chakraStyles={getChakraReactSelectStyles(selectProps?.size, { controlBorderColor })}
        className='react-select'
        components={isVirtual ? customComponents : undefined}
        defaultValue={defaultValue}
        inputId={controlId ?? inputId}
        instanceId={`select-instance-${name}`}
        name={name}
        openMenuOnFocus={openMenuOnFocus}
        options={options}
        placeholder={placeholder}
        required={isRequired}
        isSearchable
        {...selectProps}
      />
    );
  }
);

SelectInput.displayName = 'SelectInput';

const customComponents = {
  MenuList: ({ children, maxHeight }: any) => {
    const { t } = useI18n();
    const itemHeight = 48;
    // Note: normalize to an array so a menu holding a single match is still indexable.
    const items = Children.toArray(children);
    const itemCount = items.length;
    const wrapperHeight = maxHeight < itemCount * itemHeight ? maxHeight : itemCount * itemHeight;
    const scrollToIndex = findSelectedOptionIndex(items);

    function renderItem(itemInfo: ItemInfo) {
      return (
        <Box key={itemInfo.index} style={itemInfo.style} whiteSpace='nowrap'>
          {items[itemInfo.index]}
        </Box>
      );
    }

    if (countOptionElements(items) === 0) {
      return (
        <Box bg='white' border='solid 1px' borderColor='gray.600' p='16px 24px' rounded='base' shadow='base'>
          {t('common.noOptionsAvailable')}
        </Box>
      );
    }

    return (
      <Box bg='white' border='solid 1px' borderColor='gray.600' overflow='hidden' rounded='base' shadow='base'>
        <VirtualList
          height={wrapperHeight}
          itemCount={itemCount}
          itemSize={itemHeight}
          renderItem={renderItem}
          scrollToIndex={scrollToIndex}
          width='100%'
        />
      </Box>
    );
  },
};

export function getChakraReactSelectStyles<T>(
  size?: SizeProp,
  options?: { controlBorderColor?: string }
): ChakraStylesConfig<T> {
  return {
    valueContainer: (provided) => ({
      ...provided,
      padding: size === 'sm' ? '0 0 0 12px' : '0 1rem',
      height: '100%',
      textAlign: 'left',
      fontSize: size === 'sm' ? 'sm' : 'md',
      fontWeight: 500,
      color: '#2D3748',
    }),
    input: (provided) => ({
      ...provided,
      fontSize: size === 'sm' ? 'sm' : '16px',
    }),
    indicatorSeparator: (provided) => ({
      ...provided,
      display: 'none',
    }),
    dropdownIndicator: (provided, { selectProps }) => ({
      ...provided,
      background: 'white',
      border: 'none',
      '> svg': {
        transition: '300ms ease all',
        transform: `rotate(${selectProps.menuIsOpen ? 180 : 0}deg)`,
      },
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 3,
      overflow: 'visible',
    }),
    menuList: (provided) => ({
      ...provided,
      p: 2,
      borderColor: 'grey.200',
      shadow: 'base',
      rounded: 'base',
    }),
    control: (provided) => ({
      ...provided,
      borderColor: options?.controlBorderColor ?? 'grey.200',
      height: size === 'sm' ? '32px' : provided.height,
    }),
    placeholder: (provided) => ({
      ...provided,
      color: 'gray.500',
      whiteSpace: 'nowrap',
    }),
    option: (provided, { isSelected, isFocused }) => ({
      ...provided,
      padding: '0.75rem 1rem',
      fontWeight: 400,
      ...(isSelected && {
        bgColor: 'blue.50',
        borderLeft: '4px solid',
        borderLeftColor: 'blue.500',
        color: 'black',
        _hover: {
          bgColor: 'blue.50',
        },
      }),
      ...(isFocused && {
        bgColor: 'blue.50',
      }),
      ...(!isSelected && {
        _hover: {
          bgColor: 'blue.50',
          shadow: 'none',
        },
      }),
    }),
  };
}
